import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { useCart } from '../../context/CartContext';
import { useDataContext } from '../../context/DataContext';
import { APP_API_URL } from '../../utils';
import OrderLayout from '../../layout/order-layout';
import UIDropdown from '../../components/ui/ui-dropdown';
import UICustomerDropdown from '../../components/ui/ui-customer-dropdown';
import Add from '../customers/add';
import OrdersList from './orders-list';
import formatNumber, { calculateTaxItems, getVariantMagasinLabel } from '../../utils/functions';
import ProsFormatList from '../../components/ProsFormatList';
import {
  AIB_OPTIONS,
  CART_EDIT_KEY,
  PAYMENT_OPTIONS,
  cartItemFromVariant,
} from './cart-edit';

const Orders = () => {
  const navigate = useNavigate();
  const { categories } = useDataContext();
  const {
    cart,
    setCart,
    selectedCustomer,
    setSelectedCustomer,
    remise,
    setRemise,
    fraisLivraison,
    setTotal,
    total,
    subTotal,
    setSubTotal,
    editingSale,
    setEditingSale,
    editingProforma,
    setEditingProforma,
  } = useCart();
  const [categoriesData, setCategoriesData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPaiement, setSelectedPaiement] = useState({ label: 'Espèces', value: 4 });
  const [selectedAIBTax, setSelectedAIBTax] = useState({ label: 'AIB (0%)', value: null, emcf: null });
  const [remisePercentage, setRemisePercentage] = useState(0);
  const [remiseFixed, setRemiseFixed] = useState(0);
  const [loading, setLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(true);
  const [refreshOrder, setRefreshOrder] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const user = useSelector(state => state.auth.user);
  const [showReferencePiece, setShowReferencePiece] = useState(false);
  const [referencePiece, setReferencePiece] = useState('');
  const [useBalance, setUseBalance] = useState(false);
  const [finalizingInstallment, setFinalizingInstallment] = useState(null);
  const [showOrdersListModal, setShowOrdersListModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [sendInvoiceByEmail, setSendInvoiceByEmail] = useState(false);

  useEffect(() => {
    const email = selectedCustomer?.customer?.email?.trim();
    setSendInvoiceByEmail(!!email);
    if ((selectedCustomer?.balance ?? 0) <= 0) {
      setUseBalance(false);
    }
  }, [selectedCustomer?.value, selectedCustomer?.customer?.email, selectedCustomer?.balance]);

  useEffect(() => {
    if (categories?.length > 0) {
      setCategoriesData(categories.filter(category => category.type_categorie === null));
    }
  }, [categories]);

  const fetchProductsData = async () => {
    setProductLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/variants`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = response.data?.data || response.data || [];
      setProductsData(rows);
      setFilteredProducts(rows);
    } catch (error) {
      console.error('Erreur lors de la récupération des variants :', error);
    } finally {
      setProductLoading(false);
    }
  };

  const mapCustomerOption = (item) => ({
    label: item.name,
    value: item.id,
    phone: item.numero,
    client_code: item.client_code,
    email: item.email,
    customer: item,
    balance: item.balance,
  });

  const fetchCustomers = useCallback(async (search = '') => {
    const term = (search || '').trim();
    setCustomersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {
        per_page: term ? 200 : 500,
        sort_by: 'name',
        sort_direction: 'asc',
      };
      if (term.length >= 2) {
        params.search = term;
      }
      const response = await axios.get(`${APP_API_URL}/customers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setCustomers(response.data.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des clients :', error);
      if (term.length >= 2) {
        setCustomers([]);
      }
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const handleCustomerSearch = useCallback(
    (term) => {
      if (!term) {
        fetchCustomers('');
        return;
      }
      if (term.length >= 2) {
        fetchCustomers(term);
      }
    },
    [fetchCustomers]
  );

  const customerDropdownOptions = useMemo(() => {
    const mapped = customers.map(mapCustomerOption);
    if (!selectedCustomer?.value) return mapped;
    if (mapped.some((o) => o.value === selectedCustomer.value)) return mapped;
    return [selectedCustomer, ...mapped];
  }, [customers, selectedCustomer]);

  useEffect(() => {
    fetchProductsData();
    fetchCustomers();
  }, []);

  const clearEditing = () => {
    setEditingSale(null);
    setEditingProforma(null);
    setFinalizingInstallment(null);
  };

  useEffect(() => {
    const raw = sessionStorage.getItem(CART_EDIT_KEY);
    if (!raw) return;
    sessionStorage.removeItem(CART_EDIT_KEY);
    try {
      const draft = JSON.parse(raw);
      const customer = draft.customer || {};
      setCart(draft.cart || []);
      setSelectedCustomer({
        label: customer.name || 'Client',
        value: customer.id || draft.customerId || '',
        customer,
        balance: Number(customer.balance) || 0,
      });
      setSelectedPaiement(PAYMENT_OPTIONS.find((option) => option.value === draft.modeReglementId) || PAYMENT_OPTIONS[3]);
      setShowReferencePiece([3, 7].includes(draft.modeReglementId));
      setReferencePiece(draft.referencePiece || '');
      setSelectedAIBTax(AIB_OPTIONS.find((option) => option.emcf === draft.aibValue) || AIB_OPTIONS[0]);
      setRemisePercentage(0);
      setRemiseFixed(Number(draft.remise) || 0);
      setSendInvoiceByEmail(Boolean(draft.sendInvoiceByEmail));
      if (draft.kind === 'installment') {
        setUseBalance(Boolean(draft.useCustomerBalance));
        setEditingSale(null);
        setEditingProforma(null);
        setFinalizingInstallment({
          id: draft.installmentOrderId,
          designation: draft.designation,
        });
        return;
      }
      setFinalizingInstallment(null);
      setEditingSale({ id: draft.id, designation: draft.designation });
      setEditingProforma(null);
    } catch {
      clearEditing();
    }
  }, []);

  const calculateSubtotal = () =>
    cart.reduce(
      (subtotal, product) =>
        subtotal + (product.customPrice || product.price) * product.quantity,
      0
    );

  const calculateNetAmount = () => {
    const subtotal = calculateSubtotal();
    const produitsTaxables = cart.filter(produit => produit.is_taxable);
    const produitsExonerés = cart.filter(produit => !produit.is_taxable);
    const produitsHTTaxables = produitsTaxables.map(produit => {
      const prixSansTaxe = (produit.customPrice || produit.price) / (1.18 + selectedAIBTax.value);
      return prixSansTaxe * produit.quantity;
    });
    const produitsHTExonerés = produitsExonerés.map(produit =>
      (produit.customPrice || produit.price) * produit.quantity
    );
    const montantHTTaxables = produitsHTTaxables.reduce((total, montant) => total + montant, 0);
    const montantHTExonerés = produitsHTExonerés.reduce((total, montant) => total + montant, 0);
    const montantHT = montantHTTaxables + montantHTExonerés;
    const montantTVA = montantHTTaxables * 0.18;
    const montantAIB = montantHTTaxables * selectedAIBTax.value;
    return montantHT + montantTVA + montantAIB + fraisLivraison;
  };

  const calculateTotal = () => {
    const netAmount = calculateNetAmount();
    const totalRemise = (remisePercentage / 100) * netAmount + remiseFixed;
    setRemise(totalRemise);
    return netAmount - totalRemise;
  };

  useEffect(() => {
    setTotal(calculateTotal());
  }, [cart, remisePercentage, remiseFixed, selectedAIBTax, fraisLivraison, setTotal]);

  useEffect(() => {
    setSubTotal(calculateSubtotal());
  }, [cart, setSubTotal]);

  const handleSaveProFormat = async () => {
    setLoading(true);
    const productCount = cart.length;
    const cartState = {
      cart,
      selectedCustomer,
      selectedPaiement,
      remisePercentage,
      remiseFixed,
      subTotal,
      total,
      remise,
      id: `ASB-${Date.now()}_${productCount}`,
    };

    const aibRate = selectedAIBTax.emcf === "A" ? 0.01 : selectedAIBTax.emcf === "B" ? 0.05 : null;

    const items = cartState.cart.map(e => ({
      name: `${e.sku} - ${e.product.name}${e.product.format ? ` - ${e.product.format} ${e.is_taxable ? '(B)' : '(A)'}` : ''}`,
      price: Number(e.customPrice != null ? e.customPrice : e.price) || 0,
      quantity: e.quantity,
      taxGroup: e.is_taxable ? 'B' : 'A',
    }));

    const discount = (Number(remisePercentage) / 100) * calculateNetAmount() + Number(remiseFixed || 0);
    const result = calculateTaxItems(items, aibRate, discount);

    // Préparer les produits pour le payload
    const products = cartState.cart.map(produit => {
      return {
        variant_id: produit.id,
        produit_id: produit.product.id,
        quantite: produit.quantity,
        unite: (typeof produit.unit === 'string' && produit.unit) || produit.product?.unit?.name || produit.product?.unit?.symbol || '',
        nom_produit: `${produit.sku} - ${produit.product.name}${produit.product.format ? ` - ${produit.product.format} ${produit.is_taxable ? '(B)' : '(A)'}` : ''}`,
        prix_unitaire: Number(produit.customPrice != null ? produit.customPrice : produit.price) || 0,
      };
    });

    const form = {
      user_id: user?.id,
      customer_id: cartState.selectedCustomer.value,
      montant_ht: result.totals.montantHT,
      tva: result.totals.totalTVA,
      laib: result.totals.totalAIB,
      laib_pourcentage: selectedAIBTax.emcf === "A" ? 1 : selectedAIBTax.emcf === "B" ? 5 : null,
      montant_net: result.totals.finalTTC,
      remise: result.totals.discount,
      remise_pourcentage: Number(remisePercentage) || 0,
      remise_espece: Number(remiseFixed) || 0,
    }

    // Valider les quantités
    if (cartState.cart.some(produit => produit.quantity <= 0)) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Un ou plusieurs produits ont une quantité inférieure ou égale à zéro.",
      });
      setLoading(false);
      return;
    }

    // Envoyer la requête
    try {
      const token = localStorage.getItem("token");
      const response = await axios({
        method: editingProforma ? 'put' : 'post',
        url: editingProforma ? `${APP_API_URL}/pros-format/${editingProforma.id}` : `${APP_API_URL}/pros-format`,
        data: {
          ...form,
          produits_json: products,
        },
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
      });

      const reference = response.data?.data?.reference;
      setEditingProforma(null);
      Swal.fire({
        title: editingProforma ? 'Proforma mis à jour' : 'Commande Pro Format enregistrée !',
        text: editingProforma
          ? 'Les produits du proforma ont été remplacés.'
          : 'Votre commande Pro Format a été enregistrée avec succès.',
        icon: 'success',
        confirmButtonText: 'Voir la facture',
        confirmButtonColor: '#006633',
      }).then(result => {
        if (result.isConfirmed && reference) {
          navigate('/commandes/pros-format', { state: { reference } });
        }
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error.response?.status === 422 ? "Erreur de validation" : "Erreur",
        text: error.response?.data?.error
          || (error.response?.status === 422
            ? "Certains champs sont invalides. Veuillez vérifier et réessayer."
            : "Une erreur est survenue lors de l'envoi du formulaire."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const productCount = cart.length;
    const cartState = {
      cart,
      selectedCustomer,
      selectedPaiement,
      remisePercentage,
      remiseFixed,
      subTotal,
      total,
      remise,
      id: `ASB-${Date.now()}_${productCount}`,
      isProFormat: false,
    };

    const aibRate = selectedAIBTax.emcf === "A" ? 0.01 : selectedAIBTax.emcf === "B" ? 0.05 : null;

    const items = cartState.cart.map(e => {
      const price = Number(e.customPrice != null ? e.customPrice : e.price) || 0;
      const isTaxable = e.is_taxable ?? true;

      return {
        name: `${e.sku} - ${e.product.name}${e.product.format
          ? ` - ${e.product.format} ${isTaxable ? '(B)' : '(A)'}`
          : ` ${isTaxable ? '(B)' : '(A)'}`
          }`,
        price,
        quantity: e.quantity,
        taxGroup: isTaxable ? 'B' : 'A',
      };
    });

    const result = calculateTaxItems(items, aibRate, 0);
    // console.log(result);

    // Préparer les produits pour le payload
    const nouveauxProduits = cartState.cart.map(produit => {
      return {
        variant_id: produit.id,
        quantite: produit.quantity,
        prix_unitaire_achat: produit.price,
        unite: (typeof produit.unit === 'string' && produit.unit) || produit.product?.unit?.name || produit.product?.unit?.symbol || '',
        produit_id: produit.product.id,
        name: `${produit.sku} - ${produit.product.name}${produit.product.format ? ` - ${produit.product.format} ${produit.is_taxable ? '(B)' : '(A)'}` : ''}`,
        p2: Number(produit.customPrice != null ? produit.customPrice : produit.price) || 0,
      };
    });

    // Construire le formData
    const formData = {
      designation: cartState.id,
      telephone: cartState.selectedCustomer.customer.numero,
      remise: result.totals.discount,
      prix_unitaire: 0,
      type_commande: "customer",
      statut_commande: "en cours",
      remarques: "",
      date_livraison: "",
      adresse_livraison: "",
      customer_id: cartState.selectedCustomer.value,
      mode_reglement_id: cartState?.selectedPaiement?.value,
      user_id: user?.id,
      store_id: user?.stores[0]?.id || user?.personnels[0]?.store_id,
      produits: nouveauxProduits,
      date_livraison_prevue: "",
      reference_piece: referencePiece,
      tva: result.totals.totalTVA, // 3569, arrondi supérieur
      aib: result.totals.totalAIB, // 1772, arrondi supérieur
      aib_value: selectedAIBTax.emcf, // "B" pour 5 %
      montant_ht: result.totals.montantHT, // 35431 - 1772
      montant_net: result.totals.finalTTC, // 40772 - 1772 (si pas de remise)
      use_customer_balance: useBalance,
      send_invoice_by_email: sendInvoiceByEmail && !!selectedCustomer?.customer?.email?.trim(),
      ...(finalizingInstallment ? { installment_order_id: finalizingInstallment.id } : {}),
    };

    // Valider les quantités
    if (cartState.cart.some(produit => produit.quantity <= 0)) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Un ou plusieurs produits ont une quantité inférieure ou égale à zéro.",
      });
      setLoading(false);
      return;
    }

    // Envoyer la requête
    try {
      const token = localStorage.getItem("token");
      if (editingSale) {
        const { designation, use_customer_balance, ...updatePayload } = formData;
        await axios.put(`${APP_API_URL}/commandes/${editingSale.id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${APP_API_URL}/commandes`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      Swal.fire({
        icon: "success",
        title: "Succès !",
        text: editingSale
          ? `La commande ${editingSale.designation} a été mise à jour.`
          : finalizingInstallment
            ? `La vente ${finalizingInstallment.designation} est reprise en commande. Le solde client a été utilisé s'il était coché.`
            : "La commande a été enregistrée avec succès.",
        timer: 2000,
        showConfirmButton: false,
      });
      setEditingSale(null);
      setFinalizingInstallment(null);
      setCart([]);
      localStorage.removeItem('cart');
      setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
      setSendInvoiceByEmail(false);
      setUseBalance(false);
      setSelectedPaiement({ label: 'Espèces', value: 4 });
      setSelectedAIBTax({ label: 'AIB (0%)', value: null, emcf: null });
      setShowReferencePiece(false);
      setReferencePiece('');
      setRemisePercentage(0);
      setRemiseFixed(0);
      setRemise(0);
      setSubTotal(0);
      setTotal(0);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: error.response?.status === 422 ? "Erreur de validation" : "Erreur",
        text: error.response?.data?.error
          || (error.response?.status === 422
            ? "Certains champs sont invalides. Veuillez vérifier et réessayer."
            : "Une erreur est survenue lors de l'envoi du formulaire."),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWithPartialPayment = async () => {
    setLoading(true);
    const productCount = cart.length;
    const cartState = {
      cart,
      selectedCustomer,
      selectedPaiement,
      remisePercentage,
      remiseFixed,
      subTotal,
      total,
      remise,
      id: `ASB-${Date.now()}_${productCount}`,
      isProFormat: false,
    };

    const aibRate = selectedAIBTax.emcf === "A" ? 0.01 : selectedAIBTax.emcf === "B" ? 0.05 : null;

    const items = cartState.cart.map(e => ({
      name: `${e.sku} - ${e.product.name}${e.product.format ? ` - ${e.product.format} ${e.is_taxable ? '(B)' : '(A)'}` : ''}`,
      price: Number(e.customPrice != null ? e.customPrice : e.price) || 0,
      quantity: e.quantity,
      taxGroup: e.is_taxable ? 'B' : 'A',
    }));

    const order = calculateTaxItems(items, aibRate, 0);

    const products = cartState.cart.map(produit => ({
      variant_id: produit.id,
      quantity: produit.quantity,
      unit_price: produit.price,
      modified_unit_selling_price: produit.customPrice || produit.price,
    }));

    if (cartState.cart.some(produit => produit.quantity <= 0)) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Un ou plusieurs produits ont une quantité inférieure ou égale à zéro.',
      });
      setLoading(false);
      return;
    }

    const result = await Swal.fire({
      title: 'Paiement partiel',
      input: 'number',
      inputLabel: `Montant total ${formatNumber(order.totals.finalTTC)} FCFA`,
      inputPlaceholder: 'Entrez le montant payé',
      inputAttributes: { min: 0, step: 0.01 },
      showCancelButton: true,
      confirmButtonText: 'Enregistrer',
      cancelButtonText: 'Annuler',
      preConfirm: avancePayee => {
        const avance = parseFloat(avancePayee);
        if (isNaN(avance) || avance <= 0 || avance >= order.totals.finalTTC) {
          Swal.showValidationMessage('Saisissez une avance supérieure à 0 et inférieure au total. Pour tout payer, utilisez Valider.');
        }
        return avance;
      },
    });

    if (!result.isConfirmed) {
      setLoading(false);
      return;
    }

    const avancePayee = parseFloat(result.value);

    const formData = {
      designation: cartState.id,
      remise: order.totals.discount,
      status: 'en cours',
      remarques: '',
      customer_id: cartState.selectedCustomer.value,
      mode_reglement_id: cartState?.selectedPaiement?.value,
      user_id: user?.id,
      store_id: user?.stores[0]?.id || user?.personnels[0]?.store_id,
      products: products,
      date_livraison: null,
      reference_piece: referencePiece,
      amount: avancePayee,
      tva: order.totals.totalTVA, // 3569, arrondi supérieur
      aib: order.totals.totalAIB, // 1772, arrondi supérieur
      aib_value: selectedAIBTax.emcf, // "B" pour 5 %
      montant_ht: order.totals.montantHT, // 35431 - 1772
      montant_net: order.totals.finalTTC, // 40772 - 1772 (si pas de remise) 
    };

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${APP_API_URL}/installment-orders`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Swal.fire({
        icon: 'success',
        title: 'Succès!',
        text: 'La commande a été enregistrée avec succès.',
        timer: 2000,
        showConfirmButton: false,
      });
      setCart([]);
      localStorage.removeItem('cart');
      setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
      setSendInvoiceByEmail(false);
      setUseBalance(false);
      setRemisePercentage(0);
      setRemiseFixed(0);
      setRemise(0);
      setSubTotal(0);
      setTotal(0);
      navigate(`/admin/installment-orders/${response.data.data.id}`);
    } catch (error) {
      const fieldError = error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : null;
      Swal.fire({
        icon: 'error',
        title: error.response?.status === 422 ? 'Erreur de validation' : 'Erreur',
        text: error.response?.data?.message || fieldError || "Une erreur est survenue lors de l'envoi du formulaire.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (categoryId) => {
    setProductLoading(true);
    setSearchQuery('');
    setSelectedCategory(categoryId);
    setFilteredProducts(productsData.filter(product => product?.product?.category_id === categoryId));
    setProductLoading(false);
  };

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setFilteredProducts(
      productsData.filter(
        item =>
          (!selectedCategory || item?.product?.category_id === selectedCategory) &&
          (item.name.toLowerCase().includes(query.toLowerCase()) ||
            item.sku.toLowerCase().includes(query.toLowerCase()))
      )
    );
  };

  const handleCustomersSelect = (option) => {
    setSelectedCustomer(option);
  };

  const handleAddCustomer = (newCustomer) => {
    setCustomers(prev => [...prev, newCustomer]);
    setSelectedCustomer({
      label: newCustomer.name,
      value: newCustomer.id,
      customer: newCustomer,
    });
  };

  const handlePaiementSelect = (option) => {
    setSelectedPaiement(option);
    setShowReferencePiece([3, 7].includes(option.value));
  };

  const handleAIBTaxSelect = (option) => {
    setSelectedAIBTax(option);
  };

  const saveCartToLocalStorage = () => {
    if (!selectedCustomer?.value) {
      Swal.fire({
        title: 'Erreur!',
        text: 'Veuillez sélectionner un client avant de continuer.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#006633',
      });
      return;
    }
    if (cart.length === 0) {
      Swal.fire({
        title: 'Erreur!',
        text: 'Veuillez sélectionner au moins un produit avant de continuer.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#006633',
      });
      return;
    }
    const savedOrders = JSON.parse(localStorage.getItem('orders')) || [];
    const orderId = `order_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    savedOrders.push({
      orderId,
      cart,
      selectedCustomer,
      selectedPaiement,
      remisePercentage,
      remiseFixed,
      remise,
      total,
      subTotal,
      dateCreated: new Date().toISOString(),
    });
    localStorage.setItem('orders', JSON.stringify(savedOrders));
    setRefreshOrder(true);
    Swal.fire({
      title: 'Succès!',
      text: 'La commande a été enregistrée avec succès!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#006633',
    }).then(() => {
      setCart([]);
      setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
      setSendInvoiceByEmail(false);
      setSelectedPaiement({ label: 'Espèces', value: 4 });
      setRemisePercentage(0);
      setRemiseFixed(0);
      setRemise(0);
      setSubTotal(0);
      setTotal(0);
    });
  };

  const loadOrderDetails = (order) => {
    setSelectedCustomer(order.selectedCustomer);
    setSelectedPaiement(order.selectedPaiement);
    setRemisePercentage(order.remisePercentage);
    setRemiseFixed(order.remiseFixed);
    setRemise(order.remise);
    setCart(order.cart);
    setSubTotal(order.subTotal);
    setTotal(order.total);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const productInCart = prev.find(item => item.id === product.id);
      if (productInCart) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...prev,
        {
          ...product,
          quantity: 1,
          initialQuantity: product.quantity,
          is_taxable: true
        }
      ];
    });
  };

  const updateQuantity = (productId, quantity) => {
    setCart(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity: Math.max(0, quantity) } : item
      )
    );
  };

  const updateProductPrice = (productId, newPrice) => {
    setCart(prev =>
      prev.map(product =>
        product.id === productId ? { ...product, customPrice: newPrice } : product
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const isProductInCart = (product) => cart.some(item => item.id === product.id);

  const resetFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    fetchProductsData();
    setCart([]);
    localStorage.removeItem('cart');
    setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
    setSendInvoiceByEmail(false);
    setSelectedPaiement({ label: 'Espèces', value: 4 });
    setRemisePercentage(0);
    setRemiseFixed(0);
    setRemise(0);
  };

  function handleQuantityChange(e, product, updateQuantity) {
    let enteredQuantity = e.target.value;
    const isIntegerOnly = product.product.unit_id === 5;

    // Validation : autoriser ou interdire les nombres à virgule
    if (isIntegerOnly) {
      if (!/^\d*$/.test(enteredQuantity)) {
        return; // Bloque l'entrée si ce n'est pas un entier
      }
      enteredQuantity = parseInt(enteredQuantity, 10);
    } else {
      if (!/^\d*\.?\d*$/.test(enteredQuantity)) {
        return; // Bloque l'entrée si ce n'est pas un nombre valide (float)
      }
      enteredQuantity = parseFloat(enteredQuantity);
    }

    let message = '';
    let error = '';

    // Vérification des limites
    if (
      enteredQuantity > product.initialQuantity ||
      enteredQuantity < 0 ||
      isNaN(enteredQuantity)
    ) {
      if (enteredQuantity > product.initialQuantity) {
        error = `Quantité demandée (${enteredQuantity}) dépasse la quantité disponible (${product.initialQuantity}).`;
      }
      message = '';
    } else {
      // Logique de calcul de cartons/pièces en fonction des m²
      const m2PerCarton = product.nb_m2_par_carton || 0;
      const m2PerPiece = product.nb_m2_par_piece || 0;

      if (product.product.category_id === 17) {
        if (m2PerCarton > 0 && m2PerPiece > 0) {
          const totalCartons = Math.floor(enteredQuantity / m2PerCarton);
          const remainingM2 = enteredQuantity % m2PerCarton;
          const totalPieces = Math.floor(remainingM2 / m2PerPiece);

          message = `${totalCartons} carton(s)` +
            (remainingM2 > 0 && totalPieces > 0 ? ` et ${totalPieces} pièce(s)` : '');
        }
        else {
          message = `${enteredQuantity} pièce(s)`;
        }
      } else {
        message = `${enteredQuantity} pièce(s)`;
      }

      error = '';
    }

    // Mise à jour de la quantité si aucune erreur
    if (!error) {
      updateQuantity(product.id, enteredQuantity);
    }

    // Mise à jour des messages d'erreur et d'information
    const parent = e.target.parentElement;
    const errorEl = parent.querySelector('.error-message');
    const messageEl = parent.querySelector('.info-message');

    if (errorEl) errorEl.textContent = error;
    if (messageEl) messageEl.textContent = message;
  }

  const toggleTaxable = (productId) => {
    setCart(prev =>
      prev.map(item =>
        item.id === productId
          ? { ...item, is_taxable: !item.is_taxable }
          : item
      )
    );
  };

  const loadProforma = async (summary) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/pros-format/${summary.reference}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.data || response.data;
      let customerRaw = data.customer;
      if (typeof customerRaw === 'string') {
        try {
          customerRaw = JSON.parse(customerRaw);
        } catch {
          customerRaw = null;
        }
      }
      const matched = customers.find((customer) =>
        (customerRaw?.id && Number(customer.id) === Number(customerRaw.id))
        || (customerRaw?.phone && customer.numero === customerRaw.phone)
        || (customerRaw?.name && customer.name === customerRaw.name)
      );
      const items = [];
      for (const line of data.produits_json || []) {
        let variant = productsData.find((row) => Number(row.id) === Number(line.variant_id));
        if (!variant && line.variant_id) {
          const variantResponse = await axios.get(`${APP_API_URL}/variants/${line.variant_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          variant = variantResponse.data?.data || variantResponse.data;
        }
        if (!variant?.id) continue;
        items.push(cartItemFromVariant(variant, line));
      }
      if (!items.length) {
        Swal.fire({
          icon: 'error',
          title: 'Produits introuvables',
          text: 'Impossible de recharger les articles de ce proforma.',
        });
        return;
      }
      setCart(items);
      if (matched) {
        setSelectedCustomer({
          label: matched.name,
          value: matched.id,
          customer: matched,
          balance: Number(matched.balance) || 0,
        });
      } else if (customerRaw?.name) {
        setSelectedCustomer({
          label: customerRaw.name,
          value: customerRaw.id || '',
          customer: customerRaw,
          balance: 0,
        });
      }
      setRemisePercentage(Number(data.remise_pourcentage) || 0);
      setRemiseFixed(Number(data.remise_espece) || 0);
      const aibCode = Number(data.laib_pourcentage) === 1 ? 'A' : Number(data.laib_pourcentage) === 5 ? 'B' : null;
      setSelectedAIBTax(AIB_OPTIONS.find((option) => option.emcf === aibCode) || AIB_OPTIONS[0]);
      setEditingProforma({ id: data.id, reference: data.reference });
      setEditingSale(null);
      Swal.fire({
        icon: 'success',
        title: 'Proforma chargé',
        text: `${data.reference} est dans le panier. Vous pouvez changer les produits.`,
        timer: 1800,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Chargement impossible',
        text: 'Ce proforma n\'a pas pu être ouvert dans le panier.',
      });
    }
  };

  const handleWheel = (e) => e.target.blur();
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
  };

  return (
    <OrderLayout>
      <div className="duo-caisse-layout">
        <div className="duo-caisse-products-col">
          <div className="duo-caisse-products-header">
          <div className="duo-caisse-toolbar">
            <button
              type="button"
              className="duo-btn duo-btn--ghost"
              onClick={() => setShowOrdersListModal(true)}
            >
              <i className="bi bi-cart" /> Commandes
            </button>
            <ProsFormatList refreshOrder={refreshOrder} onEdit={loadProforma} />
            <input
              type="search"
              className="duo-input"
              value={searchQuery}
              onChange={handleSearch}
              placeholder="Rechercher un produit"
            />
            <button type="button" title="Rafraîchir" onClick={resetFilters} className="duo-btn duo-btn--ghost duo-btn--icon">
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>

          <h2 className="duo-caisse-title">Catégories</h2>
          <p className="duo-caisse-subtitle">Sélectionnez une catégorie</p>

          <div className="duo-category-scroll">
            {categoriesData.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`duo-category-chip${selectedCategory === item.id ? ' is-active' : ''}`}
                onClick={() => handleCategoryClick(item.id)}
              >
                {item.nom}
              </button>
            ))}
          </div>

          <h2 className="duo-caisse-title mb-2">Produits</h2>
          </div>

          <div className="duo-caisse-products-scroll">
          {productLoading ? (
            <div className="duo-product-grid">
              {[...Array(8)].map((_, index) => (
                <div className="duo-skeleton-card" key={`skeleton-${index}`} style={{ minHeight: 100 }} />
              ))}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <img src="/assets/img/no-product.png" width={280} height={280} alt="Aucun produit" />
            </div>
          ) : (
            <div className="duo-product-grid">
              {filteredProducts.map((product) => (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => addToCart(product)}
                  onKeyDown={(e) => e.key === 'Enter' && addToCart(product)}
                  key={`product-${product.id}`}
                  className={`duo-product-card${isProductInCart(product) ? ' in-cart' : ''}`}
                >
                  <span className="duo-product-card__check" aria-hidden>
                    <i className="bi bi-check-lg" />
                  </span>
                  <p className="duo-product-card__name">{product.name}</p>
                  <p className="duo-product-card__sku">{product.sku}</p>
                  {getVariantMagasinLabel(product) && (
                    <span className="duo-product-card__magasin">{getVariantMagasinLabel(product)}</span>
                  )}
                  <div className="duo-product-card__meta">
                    <span className="duo-product-card__stock">
                      {product.quantity} {product.product?.unit?.symbol}
                    </span>
                    <span className="duo-product-card__price">{formatNumber(product.price)} FCFA</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="duo-caisse-cart-col">
          <div className="duo-cart-panel">
            <div className="duo-cart-panel__inner">
              <div className="duo-cart-panel__head">
                <div>
                  <h2>
                    Panier
                    <span className="duo-cart-count">{cart.length}</span>
                  </h2>
                  {(editingSale || editingProforma || finalizingInstallment) && (
                    <p className="mb-0 mt-1" style={{ fontSize: '0.8rem', color: '#006633' }}>
                      {finalizingInstallment
                        ? `Finalisation de ${finalizingInstallment.designation}. Le client peut changer les produits. Les acomptes sont sur son solde.`
                        : editingSale
                        ? `Modification de ${editingSale.designation}. Le client peut changer les produits.`
                        : `Modification du proforma ${editingProforma.reference}. Le client peut changer les produits.`}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  className="duo-btn duo-btn--ghost duo-btn--sm"
                  style={{ color: '#ff4b4b', borderColor: '#ff4b4b' }}
                  onClick={() => {
                    setCart([]);
                    localStorage.removeItem('cart');
                    setSelectedCustomer({ label: 'Sélectionner un client', value: '' });
                    setSendInvoiceByEmail(false);
                    clearEditing();
                  }}
                >
                  <i className="bi bi-x" /> Vider
                </button>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="duo-form-label mb-0">Client</label>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost duo-btn--sm"
                    title="Ajouter un client"
                    onClick={() => setShowAddCustomerModal(true)}
                  >
                    <i className="bi bi-plus" /> Nouveau
                  </button>
                </div>
              <UICustomerDropdown
                options={customerDropdownOptions}
                selectedOption={selectedCustomer}
                onSelect={handleCustomersSelect}
                enableSearch
                serverSearch
                searchLoading={customersLoading}
                onSearchChange={handleCustomerSearch}
              />
              <p
                className={`duo-info-hint${
                  selectedCustomer?.value
                    ? selectedCustomer.customer?.ifu
                      ? ' duo-info-hint--success'
                      : ' duo-info-hint--warn'
                    : ''
                }`}
              >
                {!selectedCustomer?.value ? (
                  <>Facture normalisée à la demande du client. IFU entreprise&nbsp;: bouton <strong>Nouveau</strong>.</>
                ) : selectedCustomer.customer?.ifu ? (
                  <>IFU&nbsp;: <strong>{selectedCustomer.customer.ifu}</strong></>
                ) : (
                  <>Pas d&apos;IFU — compléter la fiche pour facture entreprise.</>
                )}
              </p>
              {selectedCustomer?.value && (selectedCustomer.balance ?? 0) > 0 && (
                <p className="text-end mb-0" style={{ fontSize: '0.8rem', fontWeight: 700, color: '#006633' }}>
                  Solde : {formatNumber(selectedCustomer.balance)} FCFA
                </p>
              )}
              {selectedCustomer?.value && (
                <label
                  className="d-flex align-items-start gap-2 mt-2 mb-0"
                  style={{ fontSize: '0.72rem', cursor: selectedCustomer.customer?.email ? 'pointer' : 'not-allowed' }}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={sendInvoiceByEmail}
                    onChange={(e) => setSendInvoiceByEmail(e.target.checked)}
                    disabled={!selectedCustomer.customer?.email?.trim()}
                  />
                  <span>
                    Envoyer la facture normalisée par email
                    {selectedCustomer.customer?.email?.trim()
                      ? ` (${selectedCustomer.customer.email})`
                      : ' — renseignez l\'email du client'}
                  </span>
                </label>
              )}
            </div>

            <div className="max-h-60 overflow-y-auto" style={{ marginBottom: '0.75rem' }}>
              {cart.length === 0 ? (
                <div className='h-full flex flex-row justify-center items-center text-center'>
                  <img
                    src='/assets/img/no-product.png'
                    width={200}
                    height={200}
                    alt='no-products'
                  />
                </div>
              ) : (
                cart.map((product) => (
                    <div className="duo-cart-item" key={product.id}>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <span className="duo-cart-item__name">{product.name}</span>
                          {getVariantMagasinLabel(product) && (
                            <span className="duo-cart-item__magasin">{getVariantMagasinLabel(product)}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          title="Supprimer"
                          className="duo-btn duo-btn--ghost duo-btn--sm"
                          onClick={() => removeFromCart(product.id)}
                        >
                          <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                        </button>
                      </div>
                      <div className="row g-2">
                            <div className="col-6">
                              <label className="duo-form-label">Quantité</label>
                              <input
                                type="number"
                                className="duo-input"
                                id='quantity'
                                name='quantity'
                                min={0}
                                value={product.quantity}
                                max={product.initialQuantity}
                                onWheel={handleWheel}
                                onKeyDown={handleKeyDown}
                                onChange={e => handleQuantityChange(e, product, updateQuantity)}
                              />
                              <p className='text-xs text-red-600 mt-1 error-message'></p>
                              <p className='text-xs text-gray-600 mt-1 info-message'></p>
                            </div>
                            <div className="col-4">
                              <label className="duo-form-label">Prix</label>
                              <input
                                type="number"
                                className="duo-input"
                                min={0}
                                id='remiseFixed'
                                name='remiseFixed'
                                value={product.customPrice || ''}
                                placeholder={product.price}
                                onWheel={handleWheel}
                                onKeyDown={handleKeyDown}
                                onChange={e => {
                                  const newPrice = parseFloat(e.target.value);
                                  if (!isNaN(newPrice) && newPrice >= 0) {
                                    updateProductPrice(product.id, newPrice);
                                  }
                                }}
                              />
                            </div>
                            <div className="col-2 d-flex flex-column justify-content-end">
                              <label className="duo-form-label" htmlFor={`taxable-${product.id}`}>
                                TVA
                              </label>
                              <input
                                type="checkbox"
                                className="form-check-input mt-1"
                                id={`taxable-${product.id}`}
                                checked={product.is_taxable || false}
                                onChange={() => toggleTaxable(product.id)}
                              />
                            </div>
                          </div>
                          <p className="mb-0 mt-2" style={{ fontWeight: 800, fontSize: '0.85rem', color: '#006633' }}>
                            {formatNumber((product.customPrice || product.price) * (product.quantity || 0))} FCFA
                          </p>
                    </div>
                  ))
              )}
            </div>

            <div className="duo-cart-totals">
              <div className="row g-2 mb-2">
                <div className="col-12">
                  <label className="duo-form-label">Type d&apos;impôt AIB</label>
                  <UIDropdown
                    options={[
                      { label: 'AIB (0%)', value: null, emcf: null },
                      { label: 'AIB (01%)', value: 0.01, emcf: 'A' },
                      { label: 'AIB (05%)', value: 0.05, emcf: 'B' },
                    ]}
                    selectedOption={selectedAIBTax}
                    onSelect={handleAIBTaxSelect}
                    enableSearch={true}
                  />
                </div>
                <div className="col-6">
                  <label className="duo-form-label">Remise (%)</label>
                  <input
                    type="number"
                    className="duo-input"
                    min={0}
                    max={100}
                    id="remisePercentage"
                    name="remisePercentage"
                    value={remisePercentage}
                    onWheel={handleWheel}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => setRemisePercentage(parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div className="col-6">
                  <label className="duo-form-label">Remise fixe</label>
                  <input
                    type="number"
                    className="duo-input"
                    min={0}
                    id="remiseFixedOrder"
                    name="remiseFixed"
                    value={remiseFixed}
                    onWheel={handleWheel}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => setRemiseFixed(parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>

              <table>
                <tbody>
                  {remisePercentage > 0 && (
                    <tr>
                      <td style={{ color: '#ff4b4b' }}>Remise ({remisePercentage}%)</td>
                      <td style={{ color: '#ff4b4b' }}>{formatNumber((remisePercentage / 100) * calculateNetAmount())} FCFA</td>
                    </tr>
                  )}
                  {remiseFixed > 0 && (
                    <tr>
                      <td style={{ color: '#ff4b4b' }}>Remise fixe</td>
                      <td style={{ color: '#ff4b4b' }}>{formatNumber(remiseFixed)} FCFA</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ color: '#ff4b4b' }}>Remise totale</td>
                    <td style={{ color: '#ff4b4b' }}>{formatNumber(remise)} FCFA</td>
                  </tr>
                  <tr className="duo-total-row">
                    <td>Total</td>
                    <td>{formatNumber(calculateTotal())} FCFA</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <label className="duo-form-label">Méthode de paiement</label>
              <UIDropdown
                    options={[
                      { label: 'Mobile Money', value: 1 },
                      { label: 'Carte bancaire', value: 2 },
                      { label: 'Virement', value: 3 },
                      { label: 'Espèces', value: 4 },
                      { label: 'Crédit', value: 5 },
                      { label: 'Chèques', value: 7 },
                      { label: 'Autre', value: 6 },
                    ]}
                    selectedOption={selectedPaiement}
                    onSelect={handlePaiementSelect}
                enableSearch={true}
              />
              {showReferencePiece && (
                  <div className="mt-2">
                    <label htmlFor="referencePiece" className="duo-form-label">
                      Référence pièce
                    </label>
                    <input
                      type="text"
                      className="duo-input"
                      id='referencePiece'
                      name='reference_piece'
                      value={referencePiece}
                      onWheel={handleWheel}
                      onKeyDown={handleKeyDown}
                      onChange={e => setReferencePiece(e.target.value)}
                      placeholder='Saisissez la référence de la pièce'
                    />
                  </div>
                )}
            </div>

            {selectedCustomer?.value && (selectedCustomer.balance ?? 0) > 0 && (
              <div className="duo-balance-alert">
                    <strong>Solde client disponible</strong>
                      <p className="mb-1 mt-2">
                        Solde : <b>{formatNumber(selectedCustomer?.balance || 0)} FCFA</b>
                      </p>

                      {/* Total commande */}
                      {/* <p>
                        Total commande :{" "}
                        <b>{formatNumber(calculateTotal())} FCFA</b>
                      </p> */}

                      {/* Utilisation du solde */}
                      {/* {useBalance && (
                        <p>
                          Solde utilisé :{" "}
                          <b>
                            {formatNumber(
                              Math.min(
                                selectedCustomer?.balance || 0,
                                calculateTotal()
                              )
                            )}{" "}
                            FCFA
                          </b>
                        </p>
                      )} */}

                      {/* Reste à payer */}
                      <p className="mb-2">
                        Reste à payer :{' '}
                        <b>
                          {formatNumber(
                            useBalance
                              ? Math.max(calculateTotal() - (selectedCustomer?.balance || 0), 0)
                              : calculateTotal()
                          )}{' '}
                          FCFA
                        </b>
                      </p>
                      <label className="d-flex align-items-center gap-2 cursor-pointer mb-0">
                        <input
                          type="checkbox"
                          checked={useBalance}
                          onChange={() => setUseBalance(!useBalance)}
                        />
                        <span>Utiliser le solde client</span>
                      </label>
              </div>
            )}

            <div className="duo-btn-group">
              <button type="button" onClick={saveCartToLocalStorage} className="duo-btn duo-btn--orange">
                Enregistrer
              </button>
              <button
                onClick={handleSaveProFormat}
                type="button"
                className="duo-btn duo-btn--ghost"
                disabled={calculateTotal() === 0 || loading}
              >
                {editingProforma ? `Mettre à jour ${editingProforma.reference}` : 'Pro Format'}
              </button>
            </div>

            <div className="mt-3">
              <button
                type="button"
                className={`duo-btn duo-btn--primary duo-btn--block${
                  !selectedPaiement.value ||
                  calculateTotal() === 0 ||
                  !selectedCustomer?.value ||
                  (showReferencePiece && !referencePiece) ||
                  loading
                    ? ''
                    : ''
                }`}
                style={
                  !selectedPaiement.value ||
                  calculateTotal() === 0 ||
                  !selectedCustomer?.value ||
                  (showReferencePiece && !referencePiece) ||
                  loading
                    ? { opacity: 0.5, cursor: 'not-allowed', boxShadow: 'none' }
                    : undefined
                }
                onClick={handleSubmit}
                disabled={
                  !selectedPaiement.value ||
                  calculateTotal() === 0 ||
                  !selectedCustomer?.value ||
                  (showReferencePiece && !referencePiece) ||
                  loading
                }
              >
                {editingSale
                  ? `Mettre à jour ${editingSale.designation}`
                  : finalizingInstallment
                    ? `Finaliser ${finalizingInstallment.designation}`
                    : 'Valider'} — {formatNumber(calculateTotal())} FCFA
              </button>
              <button
                type="button"
                className="duo-btn duo-btn--ghost duo-btn--block mt-2"
                onClick={handleSaveWithPartialPayment}
                disabled={
                  !!finalizingInstallment ||
                  !selectedPaiement.value ||
                  calculateTotal() === 0 ||
                  !selectedCustomer?.value ||
                  (showReferencePiece && !referencePiece) ||
                  loading
                }
              >
                Payer avance
              </button>
            </div>

            </div>
          </div>
        </div>
      </div>

      <OrdersList
        open={showOrdersListModal}
        onClose={() => setShowOrdersListModal(false)}
        setOrderDetails={loadOrderDetails}
        refreshOrder={refreshOrder}
      />
      <Add
        open={showAddCustomerModal}
        onClose={() => setShowAddCustomerModal(false)}
        onAddCustomer={handleAddCustomer}
        refreshData={fetchCustomers}
      />
    </OrderLayout>
  );
};

export default Orders;